import { VOCATION_INDUSTRY, type VocationIndustry } from "../types/vocationIndustry";
import { VOCATION_SUBINDUSTRY, type VocationSubindustry } from "../types/vocationSubindustry";

/** Nullable storage shape: requirements apply only in option mapping mode. */
export interface FormFunnelOptionTarget {
  kind?: "industry" | "subindustry" | "vocation" | null;
  industries?: readonly VocationIndustry[] | null;
  subindustries?: readonly VocationSubindustry[] | null;
  vocationIds?: readonly string[] | null;
}

export interface OptionTargetQuestion {
  type?: string | null;
  filterMapping?: {
    mode?: "question" | "option" | null;
    filterKey?: string | null;
    valueMapping?: Record<string, string> | null;
    staticOptions?: boolean | null;
  } | null;
  options?:
    | readonly {
        value: string | number | boolean;
        target?: FormFunnelOptionTarget | null;
      }[]
    | null;
}

export type ResolvedFormFunnelTarget =
  | { kind: "industry"; filterKey: "program_vocation_industry"; values: VocationIndustry[] }
  | {
      kind: "subindustry";
      filterKey: "program_vocation_subindustries_in";
      values: VocationSubindustry[];
    }
  | { kind: "vocation"; filterKey: "program_vocation_in"; values: string[] };

/** One bounded taxonomy row for each vocation with qualifying programs. */
export interface AvailableVocationTarget {
  vocationId: string;
  industry: VocationIndustry;
  subindustries: readonly VocationSubindustry[];
}

export interface FormFunnelOptionAvailability {
  fieldKey: string;
  optionValues: string[];
}

const industries = new Set<string>(Object.values(VOCATION_INDUSTRY));
const subindustries = new Set<string>(Object.values(VOCATION_SUBINDUSTRY));

export function isOptionTargetQuestion(
  question: Pick<OptionTargetQuestion, "filterMapping"> | null | undefined
): boolean {
  return question?.filterMapping?.mode === "option";
}

function normalizedValues(values: unknown): string[] | null {
  if (!Array.isArray(values) || !values.length) return null;
  if (values.some((value) => typeof value !== "string" || !value.trim())) return null;
  return [...new Set((values as string[]).map((value) => value.trim()))];
}

/** Resolve one tier directly to its native filter; never expand taxonomy tiers. */
export function resolveFormFunnelTarget(
  target: FormFunnelOptionTarget | null | undefined
): ResolvedFormFunnelTarget | null {
  if (!target) return null;
  const populatedKinds = [target.industries, target.subindustries, target.vocationIds].filter(
    (values) => Array.isArray(values) && values.length > 0
  );
  if (populatedKinds.length !== 1) return null;

  switch (target.kind) {
    case "industry": {
      const values = normalizedValues(target.industries);
      if (!values || !values.every((value) => industries.has(value))) return null;
      return {
        kind: "industry",
        filterKey: "program_vocation_industry",
        values: values as VocationIndustry[],
      };
    }
    case "subindustry": {
      const values = normalizedValues(target.subindustries);
      if (!values || !values.every((value) => subindustries.has(value))) return null;
      return {
        kind: "subindustry",
        filterKey: "program_vocation_subindustries_in",
        values: values as VocationSubindustry[],
      };
    }
    case "vocation": {
      const values = normalizedValues(target.vocationIds);
      return values ? { kind: "vocation", filterKey: "program_vocation_in", values } : null;
    }
    default:
      return null;
  }
}

/** Resolve selected choices without combining the matching rules inside each target. */
export function resolveFormFunnelOptionTargets(
  question: OptionTargetQuestion,
  answer: unknown
): ResolvedFormFunnelTarget[] | null {
  if (!isOptionTargetQuestion(question)) return null;
  const values =
    typeof answer === "string"
      ? [answer]
      : question.type === "multiselect" && Array.isArray(answer)
        ? answer
        : null;
  if (!values) return null;

  const targets: ResolvedFormFunnelTarget[] = [];
  for (const value of new Set(values)) {
    if (typeof value !== "string") return null;
    const options = question.options?.filter((option) => option.value === value);
    if (options?.length !== 1) return null;
    const target = resolveFormFunnelTarget(options[0].target);
    if (!target) return null;
    targets.push(target);
  }
  return targets;
}

/**
 * Mirrors native DreamSearch membership semantics. All subindustries must occur
 * on ONE available vocation; independent facets cannot establish co-occurrence.
 */
export function isFormFunnelTargetAvailable(
  target: FormFunnelOptionTarget | ResolvedFormFunnelTarget | null | undefined,
  vocationTargets: readonly AvailableVocationTarget[]
): boolean {
  const resolved = target && "values" in target ? target : resolveFormFunnelTarget(target);
  if (!resolved) return false;
  switch (resolved.kind) {
    case "industry":
      return vocationTargets.some((vocation) => resolved.values.includes(vocation.industry));
    case "vocation":
      return vocationTargets.some((vocation) => resolved.values.includes(vocation.vocationId));
    case "subindustry":
      return vocationTargets.some((vocation) =>
        resolved.values.every((value) => vocation.subindustries.includes(value))
      );
  }
}
